import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, CircularProgress, Divider, Table, TableBody, TableCell, TableHead, TableRow, Card, } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CustomButton from "../common/CustomButton";
import CustomTextField from "../common/CustomTextField";
import CustomTextarea from "../common/CustomTextarea";
import { genericPost } from '../../services/authServices';
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomPrefixSuffixField from "../common/CustomPrefixSuffixField";
import CustomStyle from "../common/CustomStyle";
import { t } from "../../constants/translationHelper";
import axios from "axios";
import { getClientCompany, getFCLData, } from "../../services/CommonServices";
import {
  GET_PRODUCTS_LIST,
  READ_ORDER_PLANNING,
  UPDATE_ORDER_PLANNING,
  DELETE_ORDER_PLANNING_PARTICULAR
} from "../../services/apiRoutes";

const ValidationSchema = Yup.object().shape({
  commanShipmentId: Yup.string().required('Order Type is required'),
  orderNo: Yup.string().required('Order No is required'),
  clientId: Yup.number().required('Client is required'),
  currencyId: Yup.number().required('Currency is required'),
  status: Yup.string().required('Status is required'),
  date: Yup.date().required('Date is required'),
  eTD: Yup.date().required('Expected ETD is required'),
  eTA: Yup.date()
    .required('Expected ETA is required')
    .min(
      Yup.ref('eTD'),
      'ETA must be greater than or equal to ETD'
    ),
  particulars: Yup.array()
    .of(
      Yup.object().shape({
        productName: Yup.string().required('Product Name is required'),
        qty: Yup.number()
          .required('Qty is required')
          .positive('Qty must be greater than 0'),
        rate: Yup.number()
          .required('Rate is required')
          .positive('Rate must be greater than 0'),
        unit: Yup.string().required('Unit is required'),
        noOfContainer: Yup.number()
          .required('No Of Container is required')
          .positive('No Of Container must be greater than 0'),
        containerSize: Yup.string().required('Container Size is required'),
      })
    )
    .min(1, 'At least one product is required')
    .required('Particulars are required'),
});

const UpdateOrderPlanning = ({ handleClose, setMessage, fetchData, currencyList, selectedId, statusList, orderTypeList, }) => {
  const [productsList, setProducts] = useState([]);
  const [clientCompany, setClientCompany] = useState([]);
  const [fclData, setFCLData] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedId) return;
    const fetchOrderPlanning = async () => {
      try {
        const accessToken = sessionStorage.getItem("accessToken");
        const response = await axios.get(READ_ORDER_PLANNING(selectedId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const order = response.data.orderPlanning;
        setData(order);
        if (order.clientId) {
          await fetchProductsList(order.clientId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderPlanning();
  }, [selectedId]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      const payload = {
        orderNo: values.orderNo,
        commanShipmentId: values.commanShipmentId,
        shipmentId: values.shipmentId,
        clientId: values.clientId,
        countryId: values.countryId,
        currencyId: values.currencyId,
        status: values.status,
        date: values.date,
        eTD: values.eTD,
        eTA: values.eTA,
        notes: values.notes,
        particulars: values.particulars.map((item) => ({
          productName: item.productName,
          qty: item.qty,
          rate: item.rate,
          unit: item.unit,
          noOfContainer: item.noOfContainer,
          containerSize: item.containerSize,
          shippingMark: item.shippingMark,
          description: item.description || "",

        })),
      };
      const response = await axios.put(UPDATE_ORDER_PLANNING(selectedId), payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 200) {
        setMessage({
          open: true,
          text:
            response.data.message ||
            "Order Planning updated successfully",
          type: "success",
        });
        fetchData();
        handleClose();
      } else {
        setMessage({
          open: true,
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      setMessage({
        open: true,
        text: "Something went wrong",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fetchProductsList = async (clientCompanyId) => {
    if (!clientCompanyId) {
      setProducts([]);
      return;
    }
    setProductsLoading(true);
    const authToken = sessionStorage.getItem("accessToken");
    try {
      const response = await axios.post(
        GET_PRODUCTS_LIST,
        {
          clientCompanyId,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (response.status === 200 && response.data.success) {
        setProducts(response.data.productsList || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error(error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const getCommonData = async () => {
    const clientCompany = await getClientCompany()
    setClientCompany(clientCompany);
    const fclData = await getFCLData();
    setFCLData(fclData);
  }
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };
  const currentDate = formatDate(new Date());
  const [formData] = useState({
    orderNo: "",
    commanShipmentId: '',
    shipmentId: '',
    clientId: '',
    currencyId: '',
    status: '1',
    date: currentDate,
    eTD: '',
    eTA: '',
    notes: '',
    particulars: [
      {
        productName: '',
        qty: '',
        rate: '',
        unit: '',
        noOfContainer: '',
        containerSize: '',
        shippingMark: '',
        description: "",

      }
    ]
  });
  useEffect(() => {
    getCommonData();
  }, [])

  const handleDeleteParticular = async (
    orderParticularsId,
    index,
    arrayHelpers
  ) => {
    // New row -> remove directly
    if (!orderParticularsId) {
      arrayHelpers.remove(index);
      return;
    }

    try {
      const accessToken = sessionStorage.getItem("accessToken");

      const response = await axios.delete(
        DELETE_ORDER_PLANNING_PARTICULAR(orderParticularsId),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        arrayHelpers.remove(index);

        setMessage({
          open: true,
          text:
            response.data.message ||
            "Particular deleted successfully.",
          type: "success",
        });
      }
    } catch (error) {
      setMessage({
        open: true,
        text:
          error?.response?.data?.message ||
          "Failed to delete particular.",
        type: "error",
      });
    }
  };

  return (
    <Box sx={{ mx: 'auto', mt: 0 }}>
      <Formik
        enableReinitialize
        initialValues={{
          orderNo: data?.orderNo || "",
          commanShipmentId: data?.commanShipmentId || "",
          shipmentId: data?.shipmentId || "",
          clientId: data?.clientId || "",
          currencyId: data?.currencyId || "",
          countryId: data?.countryId || "",
          status: data?.status || "",
          date: data?.date || "",
          eTD: data?.eTD || "",
          eTA: data?.eTA || "",
          notes: data?.notes || "",
          particulars:
            data?.particulars?.length > 0
              ? data.particulars.map((item) => ({
                orderParticularsId: Number(item.orderParticularsId),
                productName: Number(item.productName),
                qty: item.qty,
                rate: Number(item.rate),
                amount:
                  Number(item.qty || 0) *
                  Number(item.rate || 0),
                unit: item.unit,
                noOfContainer: item.noOfContainer,
                containerSize: Number(item.containerSize),
                shippingMark: item.shippingMark,
                description: item.description || "",
              }))
              : [
                {
                  orderParticularsId: "",
                  productName: "",
                  qty: "",
                  rate: "",
                  amount: "",
                  unit: "",
                  noOfContainer: "",
                  containerSize: "",
                  shippingMark: "",
                  description: "",
                },
              ],
        }}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
          <Form>
            <Grid container spacing={1} style={CustomStyle.container}>
              <Grid item xs={4} md={4}>
                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                  {t("Update Order Planning")}
                </Typography>
              </Grid>
              <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                <Box style={CustomStyle.buttonBox}>
                  <CustomButton
                    startIcon={<SaveRounded />}
                    title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                    size="small"
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                  />
                  <CustomButton
                    startIcon={<ClearRounded />}
                    title="Cancel"
                    size="small"
                    variant="contained"
                    onClick={handleClose}
                  />
                </Box>
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={1} sx={{ px: "0%" }}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <InsertDriveFileOutlinedIcon />
                  <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                    {t("Bill To")}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomAutocomplete
                  options={clientCompany}
                  required
                  fieldLabel="Company"
                  getOptionLabel={(option) => option.title}
                  value={
                    clientCompany.find(
                      (option) => option.id === values.clientId
                    ) || null
                  }
                  onChange={async (e, company) => {
                    const companyId = company ? company.id : "";
                    setFieldValue("clientId", companyId);
                    setProducts([]);
                    const resetRows = values.particulars.map(() => ({
                      productName: "",
                      description: "",
                      qty: "",
                      rate: "",
                      amount: "",
                      unit: "",
                      noOfContainer: "",
                      containerSize: "",
                      shippingMark: ""
                    }));
                    setFieldValue("particulars", resetRows);
                    if (companyId) {
                      await fetchProductsList(companyId);
                    }
                  }}
                  placeholder="Select Company"
                  error={touched.clientId && !!errors.clientId}
                  helperText={touched.clientId && errors.clientId}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomAutocomplete
                  required={true}
                  options={orderTypeList}
                  fieldLabel="Order Type"
                  getOptionLabel={(option) => option.title}
                  value={
                    orderTypeList.find(
                      (option) => option.id === values.commanShipmentId
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    setFieldValue(
                      "commanShipmentId",
                      newValue ? newValue.id : ""
                    )
                  }
                  placeholder="Select Order Type"
                  error={touched.commanShipmentId && !!errors.commanShipmentId}
                  helperText={touched.commanShipmentId && errors.commanShipmentId}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomAutocomplete
                  required={true}
                  options={statusList}
                  fieldLabel="Status"
                  getOptionLabel={(option) => option.title}
                  value={
                    statusList.find(
                      (option) => option.id === values.status
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    setFieldValue("status", newValue ? newValue.id : "")
                  }
                  placeholder="Select Status"
                  error={touched.status && !!errors.status}
                  helperText={touched.status && errors.status}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomTextField
                  required={true}
                  name="orderNo"
                  fieldLabel="Order No"
                  placeholder="Order No"
                  value={values.orderNo}
                  onChange={handleChange}
                  error={touched.orderNo && !!errors.orderNo}
                  helperText={touched.orderNo && errors.orderNo}
                  sx={{
                    backgroundColor: "#E9ECEF"
                  }}
                  readOnly
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomAutocomplete
                  options={currencyList}
                  fieldLabel="Currency"
                  placeholder="Currency"
                  required={true}
                  getOptionLabel={(option) => option.title}
                  value={currencyList.find((option) => option.id === values.currencyId) || null}
                  onChange={(e, newValue) =>
                    setFieldValue("currencyId", newValue ? newValue.id : "")
                  }
                  error={touched.currencyId && !!errors.currencyId}
                  helperText={touched.currencyId && errors.currencyId}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomTextField
                  placeholder="Enter Date"
                  name="date"
                  fieldLabel="Date"
                  required={true}
                  type="date"
                  value={values.date}
                  onChange={handleChange}
                  error={touched.date && !!errors.date}
                  helperText={touched.date && errors.date}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomTextField
                  required={true}
                  name="eTD"
                  fieldLabel="Expected ETD"
                  type="date"
                  value={values.eTD}
                  onChange={handleChange}
                  error={touched.eTD && !!errors.eTD}
                  helperText={touched.eTD && errors.eTD}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomTextField
                  required={true}
                  name="eTA"
                  fieldLabel="Expected ETA"
                  type="date"
                  value={values.eTA}
                  onChange={handleChange}
                  error={touched.eTA && !!errors.eTA}
                  helperText={touched.eTA && errors.eTA}
                />
              </Grid>
            </Grid>
            <FieldArray
              name="particulars"
              render={arrayHelpers => (
                <>
                  <Grid item xs={12}>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <AssignmentLateOutlinedIcon />
                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                          {t('Particulars')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Box style={{ overflowX: 'auto', overflowY: 'auto' }}>
                      <Table style={{ width: "100%" }}>
                        <TableHead>
                          <TableRow style={{ height: '40px', backgroundColor: "#e7f0f7" }}>
                            <TableCell style={{ ...styles.tablecell, width: "50px" }} rowSpan={2}>{t('No.')}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "300px" }} rowSpan={2}>{t("Particular")}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t('Qty')}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t('Rate')}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t('Unit')}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t("Amount")}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t("No Of Container")}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t("Container Size")}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "100px" }} rowSpan={2}>{t("Shipping Mark")}</TableCell>
                            <TableCell style={{ ...styles.tablecell, width: "50px" }} rowSpan={2}>{t('Action')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values.particulars.map((particular, index) => {
                            const handleQtyChange = (e) => {
                              const newQty = parseFloat(e.target.value) || 0;
                              const newRate = parseFloat(particular.rate) || 0;
                              const newAmount = (newQty * newRate).toFixed(2);
                              setFieldValue(`particulars.${index}.qty`, newQty);
                              setFieldValue(`particulars.${index}.amount`, newAmount);
                            };
                            const handleRateChange = (e) => {
                              const newRate = parseFloat(e.target.value) || 0;
                              const newQty = parseFloat(particular.qty) || 0;
                              const newAmount = (newQty * newRate).toFixed(2);
                              setFieldValue(`particulars.${index}.rate`, newRate);
                              setFieldValue(`particulars.${index}.amount`, newAmount);
                            };
                            return (
                              <TableRow key={index}>
                                <TableCell style={styles.tablecell}>{index + 1}</TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomAutocomplete
                                    options={productsList}
                                    required
                                    disabled={!values.clientId || productsLoading}
                                    loading={productsLoading}
                                    getOptionLabel={(option) => option.productName || ""}
                                    value={
                                      productsList.find(
                                        (item) => item.productsId === particular.productName
                                      ) || null
                                    }
                                    onChange={(e, product) => {
                                      setFieldValue(
                                        `particulars.${index}.productName`,
                                        product?.productsId || ""
                                      );
                                      setFieldValue(
                                        `particulars.${index}.unit`,
                                        product?.productsUnit?.productsUnitId || ""
                                      );
                                      setFieldValue(
                                        `particulars.${index}.rate`,
                                        product?.rate || ""
                                      );
                                      setFieldValue(
                                        `particulars.${index}.description`,
                                        product?.description || ""
                                      );
                                    }}
                                    placeholder={
                                      !values.clientId
                                        ? "Select Company First"
                                        : productsLoading
                                          ? "Loading Products..."
                                          : "Select Product"
                                    }
                                    error={
                                      touched.particulars?.[index]?.productName &&
                                      !!errors.particulars?.[index]?.productName
                                    }
                                    helperText={
                                      touched.particulars?.[index]?.productName &&
                                      errors.particulars?.[index]?.productName
                                    }
                                  />
                                  <CustomTextarea
                                    name={`particulars.${index}.description`}
                                    value={particular?.description || ''}
                                    onChange={handleChange}
                                    placeholder="Enter Description"
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomTextField
                                    name={`particulars.${index}.qty`}
                                    type="number"
                                    value={particular.qty}
                                    onChange={handleQtyChange}
                                    placeholder="Quantity"
                                    required={true}
                                    error={touched.particulars?.[index]?.qty && !!errors.particulars?.[index]?.qty}
                                    helperText={touched.particulars?.[index]?.qty && errors.particulars?.[index]?.qty}
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomPrefixSuffixField
                                    prefix={currencyList.find((item) => item.id === values.currencyId)?.currencySymbol}
                                    name={`particulars.${index}.rate`}
                                    type="number"
                                    value={particular.rate}
                                    onChange={handleRateChange}
                                    placeholder="Rate"
                                    required={true}
                                    error={touched.particulars?.[index]?.rate && !!errors.particulars?.[index]?.rate}
                                    helperText={touched.particulars?.[index]?.rate && errors.particulars?.[index]?.rate}
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomAutocomplete
                                    options={productsList?.map((product) => product.productsUnit) || []}
                                    readOnly={true}
                                    required={true}
                                    getOptionLabel={(option) => option.unitName || ''}
                                    value={productsList
                                      ?.find((option) => option.productsId === particular?.productName)
                                      ?.productsUnit || null}
                                    onChange={(e, newValue) =>
                                      setFieldValue(`particulars.${index}.unit`, newValue ? newValue.productsUnitId : '')
                                    }
                                    placeholder="Unit"
                                    sx={{
                                      backgroundColor: "#E9ECEF"
                                    }}
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomPrefixSuffixField
                                    prefix={currencyList.find((item) => item.id === values.currencyId)?.currencySymbol}
                                    name={`particulars.${index}.amount`}
                                    type="number"
                                    value={particular.amount || ''}
                                    required={true}
                                    onChange={handleChange}
                                    placeholder="Amount"
                                    sx={{
                                      backgroundColor: "#E9ECEF"
                                    }}
                                    readOnly
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomTextField
                                    required={true}
                                    name={`particulars.${index}.noOfContainer`}
                                    type="number"
                                    value={particular.noOfContainer}
                                    onChange={handleChange}
                                    placeholder="Container"
                                    error={
                                      touched.particulars?.[index]?.noOfContainer &&
                                      !!errors.particulars?.[index]?.noOfContainer
                                    }
                                    helperText={
                                      touched.particulars?.[index]?.noOfContainer &&
                                      errors.particulars?.[index]?.noOfContainer
                                    }
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomAutocomplete
                                    required={true}
                                    options={fclData}
                                    getOptionLabel={(option) => option.title}
                                    value={
                                      fclData.find(
                                        (item) => item.id === particular.containerSize
                                      ) || null
                                    }
                                    onChange={(e, newValue) =>
                                      setFieldValue(
                                        `particulars.${index}.containerSize`,
                                        newValue?.id || ""
                                      )
                                    }
                                    placeholder="Container Size"
                                    error={
                                      touched.particulars?.[index]?.containerSize &&
                                      !!errors.particulars?.[index]?.containerSize
                                    }
                                    helperText={
                                      touched.particulars?.[index]?.containerSize &&
                                      errors.particulars?.[index]?.containerSize
                                    }
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <CustomTextField
                                    name={`particulars.${index}.shippingMark`}
                                    value={particular.shippingMark}
                                    onChange={handleChange}
                                    placeholder="Shipping Mark"
                                    error={
                                      touched.particulars?.[index]?.shippingMark &&
                                      !!errors.particulars?.[index]?.shippingMark
                                    }
                                    helperText={
                                      touched.particulars?.[index]?.shippingMark &&
                                      errors.particulars?.[index]?.shippingMark
                                    }
                                  />
                                </TableCell>
                                <TableCell style={styles.tablecell}>
                                  <DeleteOutlineOutlinedIcon
                                    // onClick={() => arrayHelpers.remove(index)}
                                    style={{ fontSize: "20px" }}
                                    onClick={() =>
                                      handleDeleteParticular(
                                        particular.orderParticularsId,
                                        index,
                                        arrayHelpers
                                      )
                                    }

                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableRow style={{ height: '40px' }}>
                          <TableCell style={styles.tablecell} colSpan={10}>
                            <div style={{ margin: "auto", textAlign: "center" }}>
                              <CustomButton
                                sx={Additemstyle}
                                disabled={!values.clientId}
                                startIcon={<AddOutlinedIcon />}
                                title="Add Item"
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  arrayHelpers.push({
                                    orderParticularsId: "",
                                    productName: "",
                                    description: "",
                                    qty: "",
                                    rate: "",
                                    amount: "",
                                    unit: "",
                                    noOfContainer: "",
                                    containerSize: "",
                                    shippingMark: ""
                                  })
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                        <TableRow style={{ height: '40px' }}>
                          <TableCell style={styles.tablecellfooter} colSpan={2}  > {t('Total')}</TableCell>
                          <TableCell style={styles.tablecellfooter}  >
                            {values.particulars
                              .reduce((total, particular) => total + (parseFloat(particular.qty) || 0), 0)
                            }
                          </TableCell>
                          <TableCell style={styles.tablecellfooter} colSpan={2}  > </TableCell>
                          <TableCell style={styles.tablecellfooter} >
                            {currencyList.find((item) => item.id === values.currencyId)?.currencySymbol} &nbsp;
                            {values.particulars
                              .reduce((total, particular) => total + ((particular.qty || 0) * (particular.rate || 0)), 0)
                              .toFixed(2)}
                          </TableCell>
                          <TableCell style={styles.tablecellfooter} colSpan={4} > </TableCell>
                        </TableRow>
                      </Table>
                    </Box>
                  </Grid>
                </>
              )}
            />
            <Grid container spacing={1} style={{ paddingBottom: "5px", paddingTop: "10px" }}>
              <Grid item xs={4} md={6}>
                <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                  {t("In Words")}
                </Typography>
                <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px" }}>
                  {t("Only")}
                </Typography>
                <br />
                <CustomTextarea
                  placeholder="Enter Note"
                  name="notes"
                  fieldLabel="Note"
                  value={values.notes}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={8} md={6} style={{ display: 'flex', justifyContent: 'flex-end', color: "#212529", fontWeight: 500, fontSize: "20px" }}>
                <div>
                  <Box display="flex" flexDirection="row">
                    <Box display="inline-flex" alignItems="center" pr={1}>
                      {currencyList.find((item) => item.id === values.currencyId)?.currencySymbol}
                    </Box>
                    <Box display="inline-flex" alignItems="center">
                      <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                        {t("Summary")}
                      </Typography>
                    </Box>
                  </Box>
                  <Card variant="outlined" style={{ padding: "2px" }}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Typography style={{ fontWeight: "600", fontSize: "15px", paddingRight: "5px" }}>{t("Grand Total")}:</Typography>
                      <CustomPrefixSuffixField
                        prefix={currencyList.find((item) => item.id === values.currencyId)?.currencySymbol}
                        required={true}
                        name="totalAmount"
                        value={values.particulars
                          .reduce((total, particular) => total + ((particular.qty || 0) * (particular.rate || 0)), 0)
                          .toFixed(2)}
                        readOnly={true}
                        placeholder="Total Amount"
                        sx={{
                          backgroundColor: "#E9ECEF"
                        }}
                      />
                    </Box>
                  </Card>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateOrderPlanning;

const Additemstyle = {
  color: '#212529 !important',
  textTransform: 'capitalize !important',
  borderRadius: '4px !important',
  border: '1px dashed #aecce4 !important',
  height: '30px !important',
  display: 'inline-flex !important',
  alignItems: 'center !important',
  justifyContent: 'center !important',
  backgroundColor: '#e7f0f7 !important',
  width: '100% !important',
};
const styles = {
  table: {
    minWidth: 650,
  },
  textMuted: {
    color: '#6c757d',
  },
  labelCell: {
    width: '180px',
  },
  tableContainer: {
    marginBottom: '16px',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  customTableRow: {
    height: '20px',
  },
  editButton: {
    textAlign: 'right',
  },
  showDetailsSpan: {
    padding: '8px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#212529',
    cursor: 'pointer',
  },
  expandIcon: {
    color: '#212529',
  },
  boxheading: {
    width: 70,
    height: 70,
    background: 'radial-gradient(circle, #007BFF 0, rgba(0, 134, 201, .5) 0, #007BFF 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    boxShadow: 3,
  },
  headingname: {
    paddingTop: '10px',
    fontWeight: '600',
    fontSize: '20px',
    color: '#212529',
  },
  avatarcircle: {
    backgroundColor: '#007BFF',
    width: '30px',
    height: '30px',
    fontSize: '15px',
  },
  cardtittile: {
    padding: '8px',
    paddingLeft: '35px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#212529',
  },
  tablebutton: {
    backgroundColor: '#007bff',
    color: '#ffff',
    textTransform: 'capitalize',
    borderRadius: 4,
    border: '2px solid #007bff',
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tablecell: {
    border: '1px solid #dee2e6',
    color: '#212529',
    fontWeight: 600,
    fontSize: '14px',
    textAlign: 'center',
    padding: '1px',
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  tablecellfooter: {
    border: '1px solid #dee2e6',
    color: '#212529',
    fontWeight: 600,
    fontSize: '14px',
    textAlign: 'right',
    padding: '1px',
    paddingLeft: "10px",
    paddingRight: "10px",
  },
};
